import React from 'react';

import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';

import { useQuery } from '@lumapps/base-api/react-query';
import { MODES } from '@lumapps/customizations';
import { useModes } from '@lumapps/customizations/modes';
import { Menu } from '@lumapps/lumx-menu/components/Menu';
import { REAL_SIZE_FOR_LUMX_SIZE } from '@lumapps/lumx/constants';
import { mdiMenuDown } from '@lumapps/lumx/icons';
import { IconButton, PopoverProps, Text, Theme, ButtonProps } from '@lumapps/lumx/react';
import { listUserReactions } from '@lumapps/reactions-core/api';
import { reactionsQueryKeys } from '@lumapps/reactions-core/api/query';
import type { ReactionResourceRef, ReactionType } from '@lumapps/reactions-core/api/types';
import { useSelector } from '@lumapps/redux/react';
import { Link as RouterLink } from '@lumapps/router';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { isUserProfileReactEnabled } from '@lumapps/user-profile/ducks/selector';
import { socialProfile } from '@lumapps/user-profile/routes';
import { mergeRefs } from '@lumapps/utils/react/mergeRefs';

import './index.scss';
import { REACTION_USER_LIST_MENU_MIN_RESULTS } from '../../constants';
import { REACTIONS_REACT } from '../../keys';

export interface ReactionUserListMenuProps {
    resourceRef: ReactionResourceRef;
    menuButtonRef?: React.Ref<any>;
    popoverAnchorRef?: PopoverProps['anchorRef'];
    theme: Theme;
    onSeeAll: () => void;
    hoverRef?: React.RefCallback<any>;
    shouldOpen?: boolean;
    size: ButtonProps['size'];
    reactionType?: ReactionType;
    showUserReaction: boolean;
}

const CLASSNAME_MENU = 'react-reaction-button-menu';
const CLASSNAME_LIST = 'react-button-reaction-list';

const DEFAULT_WIDTH = REAL_SIZE_FOR_LUMX_SIZE.xl;

const DEBOUNCE_POPOVER_DELAY = 500;

/**
 * Reaction user list menu
 */
export const ReactionUserListMenu = (props: ReactionUserListMenuProps) => {
    const {
        shouldOpen,
        resourceRef,
        menuButtonRef,
        popoverAnchorRef,
        theme,
        onSeeAll,
        hoverRef,
        size,
        reactionType,
        showUserReaction,
    } = props;
    const { translateKey } = useTranslate();
    const { isModeEnabled } = useModes();
    const isUserSpaceEnabled = useSelector(isUserProfileReactEnabled) || isModeEnabled(MODES.USER_PROFILE_REACT);

    const [enabled, setEnabled] = React.useState(false);
    const { isLoading, data } = useQuery({
        queryKey: reactionsQueryKeys.listReactions(resourceRef, { reactionType }),
        queryFn: () =>
            listUserReactions(resourceRef, {
                maxResults: REACTION_USER_LIST_MENU_MIN_RESULTS,
                reactionType,
            }),
        enabled,
    });

    const [isOpen, setIsOpen] = React.useState(shouldOpen);
    /**
     * Debounced the opening of the popover.
     * Avoid showing / fetching the user list when the user only wants to react.
     * */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debounceOpening = React.useCallback(debounce(setIsOpen, DEBOUNCE_POPOVER_DELAY), []);
    // Make `isOpen` update indirectly so it can also be updated with the menu `onToggle`
    React.useEffect(() => {
        debounceOpening(shouldOpen);
        if (!shouldOpen) {
            setIsOpen(false);
        }
    }, [debounceOpening, shouldOpen]);

    const onToggle = React.useMemo(
        () =>
            throttle((opening: boolean) => {
                // Update the menu local state
                setIsOpen(opening);
                if (!opening) {
                    return;
                }
                // Enable query on first open
                setEnabled(true);
            }),
        [],
    );

    return (
        <Menu.Trigger
            isOpen={isOpen}
            onToggle={onToggle}
            loadingMessage={translateKey(GLOBAL.LOADING)}
            // Override the popover anchor to be the button group
            popoverProps={{
                anchorRef: popoverAnchorRef,
                className: CLASSNAME_LIST,
                fitToAnchorWidth: false,
            }}
        >
            <Menu.TriggerButton
                ref={mergeRefs([menuButtonRef, hoverRef])}
                as={IconButton}
                className={CLASSNAME_MENU}
                emphasis="low"
                icon={mdiMenuDown}
                theme={theme}
                size={size}
                label={translateKey(REACTIONS_REACT.USER_LIST_LIKES)}
            />

            {isLoading ? (
                <Menu.Skeleton ref={hoverRef} width={DEFAULT_WIDTH}>
                    <Menu.ItemSkeleton />
                    <Menu.ItemSkeleton label={{ width: '80%' }} />
                </Menu.Skeleton>
            ) : (
                <Menu ref={hoverRef}>
                    {data?.items?.map(({ user: { userId, fullName }, reactions }) => (
                        /**
                         * TODO: Create a BaseUserLink component, in the user package that only implement the link.
                         * Use it instead of RouterLink. We cannot use the current UserLink that mess up with the MenuItem and break's accessibility.
                         * */
                        <Menu.Item as={RouterLink} to={socialProfile({ userId, isUserSpaceEnabled })} key={userId}>
                            {showUserReaction && reactions && `${reactions} `}
                            {fullName}
                        </Menu.Item>
                    ))}
                    {data?.more && (
                        <Menu.Item onClick={onSeeAll}>
                            <Text as="span" color="blue">
                                {translateKey(GLOBAL.SHOW_ALL)}
                            </Text>
                        </Menu.Item>
                    )}
                </Menu>
            )}
        </Menu.Trigger>
    );
};
